import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button, Input } from '../../components/ui';
import { Modal } from '../../components/ui/Modal';
import { FileText, Plus, Trash2, Download, Search } from 'lucide-react';

export default function ArchivesTab() {
  const [archives, setArchives] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', file: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/file-archives');
      setArchives(response.data);
    } catch (error) {
      console.error('Error fetching archives:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) return alert('الرجاء اختيار ملف');
    
    setIsSubmitting(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('file', formData.file);

    try {
      await api.post('/file-archives', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setIsModalOpen(false);
      setFormData({ title: '', file: null });
      fetchArchives();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('حدث خطأ أثناء الرفع');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الملف؟')) {
      try {
        await api.delete(`/file-archives/${id}`);
        fetchArchives();
      } catch (error) {
        console.error('Error deleting archive:', error);
      }
    }
  };

  const handleDownload = async (archive) => {
    try {
      const response = await api.get(`/file-archives/${archive.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', archive.file_name || 'file');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('حدث خطأ أثناء تحميل الملف');
    }
  };

  const filteredArchives = archives.filter(a => 
    a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.file_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="بحث باسم الملف..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-10 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
          />
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 ml-2" />
          رفع ملف جديد
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full py-8 text-center text-slate-500">جاري التحميل...</div>
        ) : filteredArchives.length === 0 ? (
          <div className="col-span-full py-8 text-center text-slate-500">لا توجد ملفات في الأرشيف</div>
        ) : (
          filteredArchives.map((archive) => (
            <div key={archive.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-4 hover:border-primary/30 transition-colors group">
              <div className="w-12 h-12 rounded-lg bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-slate-800 text-sm truncate" title={archive.title}>{archive.title}</h3>
                <p className="text-xs text-slate-500 mt-1 truncate" dir="ltr">{archive.file_name}</p>
                <div className="text-[10px] text-slate-400 mt-2">{new Date(archive.created_at).toLocaleDateString('ar-MA')}</div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => handleDownload(archive)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-md transition-colors" title="تحميل">
                  <Download className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(archive.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="حذف">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="رفع ملف للأرشيف">
        <form onSubmit={handleSubmit} className="space-y-4 p-1">
          <Input
            label="اسم الملف / الوصف"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="مثال: محضر اجتماع 2026..."
          />
          
          <div className="space-y-1.5">
            <label className="text-xs text-slate-800 ml-1">الملف (PDF, صور, مستندات)</label>
            <div className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center transition-colors cursor-pointer relative ${formData.file ? 'border-primary bg-primary/5' : 'border-slate-300 hover:bg-slate-50 hover:border-primary'}`}>
              <input
                type="file"
                required
                onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${formData.file ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                <FileText className="w-5 h-5" />
              </div>
              <span className="text-sm font-bold text-slate-700">اضغط هنا لاختيار ملف</span>
              <span className="text-xs mt-1 font-medium px-2 py-1 rounded bg-white/50 text-slate-500">
                {formData.file ? formData.file.name : 'الحد الأقصى 10 ميجابايت'}
              </span>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              حفظ
            </Button>
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)} className="flex-1">
              إلغاء
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
